import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Clock, Ticket, Users, ArrowLeft, Loader2 } from "lucide-react";
import axios from "axios";
import DahliasShell from "../dahlias/DahliasShell";
import TicketPicker, { LowStockPill } from "./TicketPicker";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function EventCard({ e }) {
  return (
    <article data-testid={`open-day-${e.slug}`}
      className={`rounded-[28px] overflow-hidden bg-white border-[3px] shadow-sm hover:-translate-y-1 transition-transform flex flex-col ${e.flagship ? "border-[var(--berry)]" : "border-[var(--berry)]/25"}`}>
      {e.image ? <img src={e.image} alt={e.title} className="w-full aspect-[4/3] object-cover" loading="lazy" /> : (
        <div className="aspect-[4/3] bg-[radial-gradient(circle_at_30%_30%,#f7d6e0,transparent_60%),radial-gradient(circle_at_70%_70%,#ffe8b3,transparent_55%),#fbf6e9] flex items-center justify-center">
          <span className="font-display text-6xl text-[var(--berry)]/60">✿</span>
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        {e.flagship && <div className="font-label text-[9px] tracking-[0.35em] uppercase text-white bg-[var(--berry)] rounded-full px-2 py-0.5 self-start">Flagship event</div>}
        <h3 className="font-display text-2xl text-[var(--dahlias-ink)] mt-2">{e.title}</h3>
        <p className="text-sm italic text-[var(--berry)] mt-1">{e.tagline}</p>
        <div className="mt-3 text-xs text-[var(--dahlias-ink)]/70 space-y-1">
          <div className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {e.date || "Date announced soon"}</div>
          {e.times && <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {e.times}</div>}
          <div className="flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5" /> {e.pricing || "Pricing announced soon"}</div>
        </div>
        <div className="mt-auto pt-4 flex items-center justify-between">
          <Link to={`/visit/open-days/${e.slug}`} className="font-label text-[10px] tracking-[0.3em] uppercase text-[var(--berry)] hover:underline" data-testid={`open-day-more-${e.slug}`}>Details →</Link>
          {e.sold_out ? <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A33030]">Sold out</span>
            : e.tickets?.open ? <Link to={`/visit/open-days/${e.slug}`} className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--berry)]" data-testid={`open-day-buy-${e.slug}`}><LowStockPill remaining={e.tickets.remaining} /> Tickets →</Link>
            : e.booking_url ? <a href={e.booking_url} className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--berry)]">Tickets →</a>
            : <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--dahlias-ink)]/50">Tickets soon</span>}
        </div>
      </div>
    </article>
  );
}

export function OpenDaysSection() {
  const [events, setEvents] = useState(null);
  useEffect(() => { axios.get(`${API}/visit/open-days`).then((r) => setEvents(r.data)).catch(() => setEvents([])); }, []);
  return (
    <section id="open-days" data-testid="visit-open-days" className="bg-white border-b-[3px] border-[var(--berry)]/30">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
        <div className="text-center">
          <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)]">✦ HERITAGE OPEN DAYS ✦</div>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--dahlias-ink)] mt-2">Three parties, one summer</h2>
          <p className="mt-2 text-sm text-[var(--dahlias-ink)]/70 max-w-xl mx-auto">Open to everyone. Dates, tickets and details land here first , sign up to HQ to hear the moment they do.</p>
        </div>
        {events === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[var(--berry)]" /></div> : (
          <div className="mt-8 grid gap-6 md:grid-cols-3">{events.map((e) => <EventCard key={e.id} e={e} />)}</div>
        )}
      </div>
    </section>
  );
}

export default function OpenDayPage() {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { axios.get(`${API}/visit/open-days/${slug}`).then((r) => setE(r.data)).catch(() => setMissing(true)); }, [slug]);
  return (
    <DahliasShell>
      <section className="bg-[var(--dahlias-cream)] min-h-[60vh]" data-testid="open-day-page">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-12">
          <Link to="/dahlias/private-pickings#open-days" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[var(--berry)]"><ArrowLeft className="w-3.5 h-3.5" /> Visit Heritage</Link>
          {missing && <p className="mt-8 text-[var(--dahlias-ink)]/70">That event isn't listed right now.</p>}
          {e && (
            <>
              <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)] mt-6">✦ HERITAGE OPEN DAY ✦</div>
              <h1 className="font-display text-4xl sm:text-5xl text-[var(--dahlias-ink)] mt-2">{e.title}</h1>
              <p className="text-lg italic text-[var(--berry)] mt-2" data-testid="open-day-tagline">{e.tagline}</p>
              <div className="mt-6 grid gap-3 sm:grid-cols-3 text-sm">
                <Info Icon={CalendarDays} label="When" value={e.date || "Date announced soon"} />
                <Info Icon={Clock} label="Times" value={e.times || "To be confirmed"} />
                <Info Icon={Ticket} label="Tickets" value={e.sold_out ? "Sold out" : e.tickets?.open ? `From ${Math.min(...e.tickets.types.map((t) => t.price)) > 0 ? `NZ$${Math.min(...e.tickets.types.map((t) => t.price)).toFixed(0)}` : "free"} · on sale now` : (e.pricing || "Announced soon")} />
              </div>
              <p className="mt-8 text-[var(--dahlias-ink)]/85 leading-relaxed text-lg">{e.description}</p>
              <h2 className="font-display text-2xl text-[var(--dahlias-ink)] mt-8">What's on</h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="open-day-activities">
                {(e.activities || []).map((a) => <li key={a} className="flex gap-2 text-sm text-[var(--dahlias-ink)]/85"><span className="text-[var(--berry)]">✿</span>{a}</li>)}
              </ul>
              <TicketPicker event={e} />
              {e.booking_url && !e.sold_out && !e.tickets?.open && <a href={e.booking_url} className="inline-block mt-8 px-6 py-3 rounded-full bg-[var(--berry)] text-white font-label text-xs tracking-[0.3em] uppercase" data-testid="open-day-tickets">Get tickets</a>}
              <p className="mt-6 text-xs italic text-[var(--dahlias-ink)]/60 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Family friendly · farm footwear recommended · weather dependent</p>
            </>
          )}
        </div>
      </section>
    </DahliasShell>
  );
}

const Info = ({ Icon, label, value }) => (
  <div className="rounded-2xl bg-white border-2 border-[var(--berry)]/20 p-4">
    <div className="font-label text-[9px] tracking-[0.3em] uppercase text-[var(--berry)] flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <div className="mt-1 text-[var(--dahlias-ink)]">{value}</div>
  </div>
);
